import React, { useState, useEffect, useCallback } from "react";
import TeclaEscEnter from "../../utils/Teclaescenter";
import { formatNumero, formatDate, calculMaObra } from "../../utils/Utilitats";
import { useUserContext } from "../../utils/UserContext";
import { API_URL } from "../../utils/config";

function nomProveidorEntitat(p) {
  if (!p) return "";
  if (p.nomProveidor || p.NomProveidor) return p.nomProveidor || p.NomProveidor;
  if (p.TipusEntitat === "Autonom" || p.TipusEntitat === "Particular") {
    const cognoms = [p.Cognom1, p.Cognom2].filter(Boolean).join(" ");
    return cognoms ? `${cognoms}, ${p.Nom}` : (p.Nom || "");
  }
  return p.NomComercial || p.Nom || "";
}

function preuEfectiuProveidor(p) {
  if (!p) return 0;
  const net = parseFloat(p.PreuNet ?? p.preuUnitari ?? p.PreuUnitari ?? 0);
  const nNet = Number.isFinite(net) ? net : 0;
  if (Number(p.Oferta) === 1) {
    const ofe = parseFloat(p.PreuOferta);
    if (Number.isFinite(ofe)) return Math.min(nNet, ofe);
  }
  return nNet;
}

function etiquetaProveidorOpcio(p) {
  const nom = nomProveidorEntitat(p) || `Proveïdor ${p.idProductesProveidors}`;
  const preu = `${formatNumero(preuEfectiuProveidor(p))} €`;
  const ref = p.RefProdProv ? ` · ${p.RefProdProv}` : "";
  return `${nom} — ${preu}${ref}`;
}

function idPartidaVal(v) {
  if (v == null || v === "" || v === "null") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function esItemApartat(v) {
  const val = v && typeof v === "object" ? v.EsApartat : v;
  return val === true || val === 1 || val === "1";
}

function compararCodiPartida(aStr, bStr) {
  const aParts = String(aStr || "").split(".");
  const bParts = String(bStr || "").split(".");
  const len = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < len; i++) {
    const aPart = i < aParts.length ? aParts[i] : "0";
    const bPart = i < bParts.length ? bParts[i] : "0";
    const aIsNumber = !isNaN(aPart);
    const bIsNumber = !isNaN(bPart);
    if (aIsNumber && bIsNumber) {
      const aNum = parseInt(aPart, 10);
      const bNum = parseInt(bPart, 10);
      if (aNum !== bNum) return aNum - bNum;
    } else {
      const comp = aPart.localeCompare(bPart);
      if (comp !== 0) return comp;
    }
  }
  return 0;
}

function recorregutNivell(partidesList, partida) {
  if (!Array.isArray(partidesList) || !partida) return [];
  const pare = partida.idPartidaPare || null;
  return partidesList
    .filter((p) => (p.idPartidaPare || null) == pare)
    .sort((a, b) => compararCodiPartida(a.Partida, b.Partida));
}

/**
 * mode: "nou" | "consultar" | "modificar" | "esborrar"
 * partida: objecte de la BD PartidesPressupost (si n'hi ha, en cas de modificar / consultar / esborrar)
 * onSave: callback quan es desa (crear o modificar)
 * onCancel: callback quan es cancel·la o es tanca
 *
 * Necessitem també info contextual (ex: la versió actual, el pressupost actual, la partida pare si és subpartida, etc.)
 * - idVersions, idPressupostos, idPartidaPare (si escau)
 *
 * Expects:
 *   <DetallsPartidaVersio
 *       mode="nou"
 *       isApartat={true}
 *       partida={partidaObject}
 *       idVersions={...}
 *       idPressupostos={...}
 *       idPartidaPare={...}  // opcional
 *       onSave={(novaPartida) => {...}}
 *       onCancel={() => {...}}
 *   />
 */
function DetallsPartidaVersio({
  mode,
  isApartat,
  pressupost,
  partida,
  versio,
  partidaPare,
  partidesList,
  onNavigate,
  onSave,
  onCancel
}) {
  // 1) Determinem si és readOnly
  const isReadOnly = mode === "consultar" || mode === "esborrar";

  // Per tancar amb ESC o Enter: ESC = onCancel
  TeclaEscEnter(onCancel, null);

  // Context
  const { empresaSeleccionada } = useUserContext();

  // Navegació: germans del mateix pare (el nivell de l'arbre), IDs comparats numèricament
  const recorregut = recorregutNivell(partidesList, partida);
  const currentIndex = partida
    ? recorregut.findIndex(
        (p) => idPartidaVal(p.idPartidesPressupost) === idPartidaVal(partida.idPartidesPressupost)
      )
    : -1;

  const anteriorPartida = () => {
    if (!recorregut.length || currentIndex < 0 || !onNavigate) return;
    const n = recorregut.length;
    onNavigate(recorregut[(currentIndex - 1 + n) % n]);
  };

  const seguentPartida = () => {
    if (!recorregut.length || currentIndex < 0 || !onNavigate) return;
    const n = recorregut.length;
    onNavigate(recorregut[(currentIndex + 1) % n]);
  };

  // --------------------------------------------------------------------
  //   BLOC 1: Partida, Quantitat, Unitats, Descripcio
  // --------------------------------------------------------------------
  const [codiPartida, setCodiPartida] = useState("");
  const [codiPartidaDefault, setCodiPartidaDefault] = useState("");
  const [esApartat, setEsApartat] = useState(esItemApartat(isApartat));
  const [quantitat, setQuantitat] = useState("1");
  const [unitatMesura, setUnitatMesura] = useState("ut");
  const [unitatOptions, setUnitatOptions] = useState([]);
  const [descripcioPartida, setDescripcioPartida] = useState("");

  // --------------------------------------------------------------------
  //   BLOC 2: Elecció producte (Material, Producte, Oferta)
  // --------------------------------------------------------------------
  const [eleccioProducte, setEleccioProducte] = useState("Material");

  const [productCatalog, setProductCatalog] = useState([]);

  // FILTRES
  const [selectedCategoria, setSelectedCategoria] = useState("");
  const [selectedTipus, setSelectedTipus] = useState("");

  // (A) Material
  const [selectedMaterial, setSelectedMaterial] = useState(null);

  // (B) Producte
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Preu proveïdor / manual
  const [preuMaterialManual, setPreuMaterialManual] = useState(0);
  const [preuMaterialProveidor, setPreuMaterialProveidor] = useState(0);
  const [preuPrioritariMaterial, setPreuPrioritariMaterial] =
    useState("Proveidor");
  const [nomProveidor, setNomProveidor] = useState("");
  const [marcaModel, setMarcaModel] = useState("");
  const [producteProveidor, setProducteProveidor] = useState(null);
  const [proveidorsOptions, setProveidorsOptions] = useState([]);
  const [quantitatProducte, setQuantitatProducte] = useState(1);
  const [preuOriginalProducte, setPreuOriginalProducte] = useState(0);

  // (C) Oferta
  const [ofertesList, setOfertesList] = useState([]);
  const [selectedOferta, setSelectedOferta] = useState(null);
  const [productesOfertaList, setProductesOfertaList] = useState([]);
  const [selectedProducteOferta, setSelectedProducteOferta] = useState(null);

  // --------------------------------------------------------------------
  //   BLOC 3: GrupMaObra i temps
  // --------------------------------------------------------------------
  const [grupMaObraList, setGrupMaObraList] = useState([]);
  const [selectedGrupMaObra, setSelectedGrupMaObra] = useState(null);
  const [duracioEstimada, setDuracioEstimada] = useState(0);
  const [unitatsTemps, setUnitatsTemps] = useState("Hores");
  const [preuMaObraManual, setPreuMaObraManual] = useState(0);
  const [preuMaObraAutomatic, setPreuMaObraAutomatic] = useState(0);
  const [preuPrioritariMaObra, setPreuPrioritariMaObra] =
    useState("Automatic");

  // --------------------------------------------------------------------
  //   BLOC 4: Preu Extra
  // --------------------------------------------------------------------
  const [preuExtra, setPreuExtra] = useState(0);

  // --------------------------------------------------------------------
  //   BLOC 5: Preu Unitari i Total
  // --------------------------------------------------------------------
  const [preuUnitari, setPreuUnitari] = useState(0);
  const [preuTotal, setPreuTotal] = useState(0);

  // -------------------- Generador codi Partida --------------------
  const incrementarSegment = useCallback((segment) => {
    if (!isNaN(segment)) {
      return String(Number(segment) + 1).padStart(segment.length, "0");
    }

    let chars = segment.split("").reverse();
    let carry = true;

    for (let i = 0; i < chars.length; i++) {
      let charCode = chars[i].charCodeAt(0);

      if (charCode >= 97 && charCode <= 122) {
        if (charCode === 122) {
          chars[i] = "a";
        } else {
          chars[i] = String.fromCharCode(charCode + 1);
          carry = false;
          break;
        }
      } else if (charCode >= 65 && charCode <= 90) {
        if (charCode === 90) {
          chars[i] = "A";
        } else {
          chars[i] = String.fromCharCode(charCode + 1);
          carry = false;
          break;
        }
      } else {
        carry = false;
        break;
      }
    }

    if (carry) chars.push("a");

    return chars.reverse().join("");
  }, []);

  const generarCodiPartida = useCallback(
    (partida, partidaPare) => {
      if (partidaPare) {
        if (!partida) {
          return `${partidaPare.Partida}.001`;
        } else {
          const segments = partida.Partida.split(".");
          const lastSegment = segments.pop();
          segments.push(incrementarSegment(lastSegment));
          return segments.join(".");
        }
      } else {
        if (!partida) {
          return "001";
        } else {
          const segments = partida.Partida.split(".");
          const lastSegment = segments.pop();
          segments.push(incrementarSegment(lastSegment));
          return segments.join(".");
        }
      }
    },
    [incrementarSegment]
  );

  // =======================================================================
  //  1) Carrega llistes (ProductCatalog, Ofertes, GrupMaObra, etc.)
  // =======================================================================
  useEffect(() => {
    const fetchProductCatalog = async () => {
      try {
        const resp = await fetch(`${API_URL}/Productes.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idEmpresa: empresaSeleccionada?.IdEmpresa || 0
          })
        });
        const data = await resp.json();
        if (data.success) {
          setProductCatalog(data.productes || []);
        } else {
          console.error("Error carregant productes:", data.message);
        }
      } catch (error) {
        console.error("Error fetchProductCatalog:", error);
      }
    };

    const fetchOfertesPressupost = async () => {
      try {
        const resp = await fetch(`${API_URL}/Ofertespressupost.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idPressupostos: pressupost.idPressupostos })
        });
        const data = await resp.json();
        if (data.success) {
          setOfertesList(data.ofertes || []);
        }
      } catch (error) {
        console.error("Error carregant ofertes:", error);
      }
    };

    const fetchProductesOfertes = async () => {
      try {
        const resp = await fetch(`${API_URL}/Productesofertapres.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idPressupostos: pressupost.idPressupostos })
        });
        const data = await resp.json();
        if (data.success) {
          setProductesOfertaList(data.productes || []);
        }
      } catch (error) {
        console.error("Error carregant productes oferta:", error);
      }
    };

    const fetchGrupMaObra = async () => {
      try {
        const resp = await fetch(`${API_URL}/Grupsmaobra.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idEmpresa: empresaSeleccionada?.IdEmpresa })
        });
        const data = await resp.json();
        if (data.success) {
          setGrupMaObraList(data.grups || []);
        }
      } catch (error) {
        console.error("Error carregant grups ma obra:", error);
      }
    };

    const fetchUnitatsEnum = async () => {
      try {
        const resp = await fetch(`${API_URL}/Unitatsmesura.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" }
        });
        const data = await resp.json();
        if (data.success) {
          setUnitatOptions(data.enumValues || ["ut", "m2"]);
        }
      } catch (error) {
        console.error("Error al fetchUnitatsEnum:", error);
        setUnitatOptions(["ut", "m2"]);
      }
    };

    fetchProductCatalog();
    fetchOfertesPressupost();
    fetchProductesOfertes();
    fetchGrupMaObra();
    fetchUnitatsEnum();
  }, [empresaSeleccionada, pressupost]);

  // =======================================================================
  //  2) fetch dades producte-proveïdor + inicialització si hi ha partida
  // =======================================================================
  const fetchDadesProducteProveidor = async (idProductesProveidors) => {
    try {
      const resp = await fetch(`${API_URL}/Dadesproducteproveidor.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idProductesProveidors })
      });
      const data = await resp.json();

      if (data.success && data.producteProveidor) {
        return data.producteProveidor;
      } else {
        console.error("Error en fetchDadesProducteProveidor:", data.message);
        return null;
      }
    } catch (error) {
      console.error("Catch error en fetchDadesProducteProveidor:", error);
      return null;
    }
  };

  useEffect(() => {
    if (partida && mode !== "nou") {
      // BLOC1
      setCodiPartida(partida.Partida || "");
      setEsApartat(esItemApartat(partida) || esItemApartat(isApartat));
      setQuantitat(
        partida.Quantitat !== undefined && partida.Quantitat !== null
          ? String(partida.Quantitat)
          : "1"
      );
      setUnitatMesura(partida.Unitats || "ut");
      setDescripcioPartida(partida.DescripcioPartida || "");

      // BLOC2
      setEleccioProducte(partida.EleccioProducte || "Material");
      setSelectedMaterial(partida.Materials_idMaterials || null);
      setSelectedProduct(partida.Productes_idProductes || null);
      setQuantitatProducte(
        partida.QuantitatProd !== undefined ? partida.QuantitatProd : 1
      );
      setPreuOriginalProducte(partida.PreuOriginalProducte || 0);
      setSelectedOferta(null);
      // molt important: inicialitzar el proveïdor de la partida
      setProducteProveidor(
        partida.Productes_Proveidors_idProductesProveidors || null
      );

      if (
        partida.ProductesOfertes_idProductesOfertes &&
        partida.EleccioProducte === "Oferta"
      ) {
        const producteOfertaTrobat = productesOfertaList.find(
          (pof) =>
            pof.idProductesOfertes ===
            partida.ProductesOfertes_idProductesOfertes
        );
        if (producteOfertaTrobat) {
          setSelectedOferta(producteOfertaTrobat.Ofertes_idOfertes);
          const ofertaTrobada = ofertesList.find(
            (oferta) =>
              oferta.idOfertes === producteOfertaTrobat.Ofertes_idOfertes
          );
          setNomProveidor(ofertaTrobada.NomProveidor);
          setMarcaModel(
            `${producteOfertaTrobat.Marca || ""} ${
              producteOfertaTrobat.Model || ""
            }`.trim()
          );
        }
      } else if (
        partida.Productes_idProductes &&
        partida.EleccioProducte !== "Oferta"
      ) {
        const producteTrobat = productCatalog.find(
          (prod) => prod.idProductes === partida.Productes_idProductes
        );
        if (producteTrobat) {
          setSelectedCategoria(producteTrobat.Categoria);
          setSelectedMaterial(producteTrobat.idMaterials);
          setSelectedTipus(producteTrobat.Tipus);
          fetchDadesProducteProveidor(
            partida.Productes_Proveidors_idProductesProveidors
          )
            .then((result) => {
              if (result) {
                setNomProveidor(result.nomProveidor || "");
                setMarcaModel(
                  `${result.marca || ""} ${result.model || ""}`.trim()
                );
              }
            })
            .catch((err) => {
              console.error(
                "Error en recuperar dades producte_proveïdor:",
                err
              );
            });
        }
      } else if (
        partida.Materials_idMaterials &&
        !partida.Productes_idProductes &&
        partida.EleccioProducte !== "Oferta"
      ) {
        const materialTrobat = productCatalog.find(
          (p) => p.idMaterials === partida.Materials_idMaterials
        );
        if (materialTrobat) {
          setSelectedCategoria(materialTrobat.Categoria);
          setSelectedTipus(materialTrobat.Tipus);
        }
      }

      setSelectedProducteOferta(
        partida.ProductesOfertes_idProductesOfertes || null
      );
      setPreuMaterialManual(partida.PreuMaterialManual || 0);
      setPreuMaterialProveidor(partida.PreuMaterialProducteProveidor || 0);
      setPreuPrioritariMaterial(
        partida.PreuPrioritariMaterial || "Proveidor"
      );

      // BLOC3
      setSelectedGrupMaObra(partida.GrupMaObra_idGrupMaObra || null);
      setDuracioEstimada(partida.DuracioEstimadaUnitat || 0);
      setUnitatsTemps(partida.UnitatsTemps || "Hores");
      setPreuMaObraManual(partida.PreuMaObraManual || 0);
      setPreuMaObraAutomatic(partida.PreuMaObra || 0);
      setPreuPrioritariMaObra(
        partida.PreuPrioritariMaObra || "Automatic"
      );

      // BLOC4
      setPreuExtra(partida.PreuExtra || 0);

      // BLOC5
      setPreuUnitari(partida.PreuUnitari || 0);
      setPreuTotal(partida.PreuTotal || 0);
    } else if (mode === "nou") {
      setCodiPartidaDefault(generarCodiPartida(partida, partidaPare));
      setQuantitat("1");
      setEsApartat(esItemApartat(isApartat));
      setUnitatMesura("ut");
      setDescripcioPartida("");
      setEleccioProducte("Material");
      setSelectedCategoria("");
      setSelectedTipus("");
      setSelectedMaterial(null);
      setProducteProveidor(null);
      setSelectedProduct(null);
      setSelectedOferta(null);
      setSelectedProducteOferta(null);
      setQuantitatProducte(1);
      setPreuMaterialManual(0);
      setPreuMaterialProveidor(0);
      setPreuOriginalProducte(0);
      setPreuPrioritariMaterial("Proveidor");
      setSelectedGrupMaObra(null);
      setDuracioEstimada(0);
      setUnitatsTemps("Hores");
      setPreuMaObraManual(0);
      setPreuMaObraAutomatic(0);
      setPreuPrioritariMaObra("Automatic");
      setPreuExtra(0);
      setPreuUnitari(0);
      setPreuTotal(0);
    }
  }, [
    partida,
    partidaPare,
    productesOfertaList,
    productCatalog,
    ofertesList,
    generarCodiPartida,
    isApartat,
    mode
  ]);

  // =======================================================================
  //  3) Lògica de filtrat: Categories, Tipus, Materials, Productes, Ofertes
  // =======================================================================
  const categoriesList = Array.from(
    new Set(productCatalog.map((p) => p.Categoria).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, "ca"));

  const tipusList = Array.from(
    new Set(
      productCatalog
        .filter((p) => !selectedCategoria || p.Categoria === selectedCategoria)
        .map((p) => p.Tipus)
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b, "ca"));

  const filteredMaterials = productCatalog
    .filter((p) => !selectedCategoria || p.Categoria === selectedCategoria)
    .filter((p) => !selectedTipus || p.Tipus === selectedTipus)
    .reduce((acc, p) => {
      if (!p.idMaterials) return acc;
      const found = acc.find((x) => x.idMaterials === p.idMaterials);
      if (!found) {
        acc.push({
          idMaterials: p.idMaterials,
          NomGeneric: p.NomGeneric,
          DescripcioMaterial: p.DescripcioMaterial,
          Categoria: p.Categoria,
          Tipus: p.Tipus
        });
      }
      return acc;
    }, [])
    .sort((a, b) => (a.NomGeneric || a.DescripcioMaterial || "").localeCompare(b.NomGeneric || b.DescripcioMaterial || "", "ca"));

  const filteredProducts = productCatalog
    .filter((p) => !selectedCategoria || p.Categoria === selectedCategoria)
    .filter((p) => !selectedTipus || p.Tipus === selectedTipus)
    .filter(
      (p) => !selectedMaterial || p.idMaterials === Number(selectedMaterial)
    )
    .sort((a, b) => `${a.Marca || ""} ${a.Model || ""}`.localeCompare(`${b.Marca || ""} ${b.Model || ""}`, "ca"));

  const filteredProductesOferta = productesOfertaList.filter(
    (pof) => !selectedOferta || pof.Ofertes_idOfertes === selectedOferta
  );

  // =======================================================================
  //  4) Preus per Material
  // =======================================================================
  useEffect(() => {
    if (isReadOnly || mode === "esborrar") return;
    if (
      eleccioProducte !== "Material" ||
      !selectedMaterial ||
      !empresaSeleccionada?.IdEmpresa
    )
      return;

    let isActive = true;

    const fetchPreuMinimMaterial = async () => {
      try {
        const resp = await fetch(`${API_URL}/Preuminimmaterial.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idMaterials: Number(selectedMaterial),
            idEmpresa: empresaSeleccionada.IdEmpresa
          })
        });

        if (!resp.ok)
          throw new Error(`Error de resposta del servidor: ${resp.status}`);

        const data = await resp.json();

        if (data.success && isActive) {
          // Sempre carreguem llista de proveïdors
          setProveidorsOptions(
            Array.isArray(data.proveidors) ? data.proveidors : []
          );

          // Només autoassignem el preu mínim si encara no hi ha proveïdor seleccionat
          if (!producteProveidor) {
            setQuantitatProducte(1);
            setPreuOriginalProducte(data.preuMinim || 0);
            setPreuMaterialProveidor(data.preuMinim || 0);
            if (preuPrioritariMaterial !== "Manual") {
              setPreuPrioritariMaterial("Proveidor");
            }
            setNomProveidor(data.nomProveidor || "");
            setMarcaModel(data.producte || "");
            setProducteProveidor(data.idProductesProveidors || null);
            setSelectedProduct(data.idProductes || null);
          }
        } else if (isActive && !producteProveidor) {
          // Només si no hi havia proveïdor, passem a mode manual / zero
          setPreuMaterialProveidor(0);
          setPreuMaterialManual(0);
          setPreuPrioritariMaterial("Manual");
          setNomProveidor("");
          setMarcaModel("");
          setProveidorsOptions([]);
        }
      } catch (error) {
        if (isActive) {
          console.error("Error en fetchPreuMinimMaterial:", error);
          if (!producteProveidor) {
            setPreuMaterialProveidor(0);
            setNomProveidor("");
            setMarcaModel("");
          }
        }
      }
    };

    fetchPreuMinimMaterial();

    return () => {
      isActive = false;
    };
  }, [
    eleccioProducte,
    selectedMaterial,
    mode,
    empresaSeleccionada?.IdEmpresa,
    isReadOnly,
    preuPrioritariMaterial,
    producteProveidor
  ]);

  // =======================================================================
  //  4b) Proveïdors amb preu del producte (com Tasques)
  // =======================================================================
  useEffect(() => {
    if (isReadOnly || mode === "esborrar") return;
    if (eleccioProducte === "Oferta") return;
    if (!selectedProduct || !empresaSeleccionada?.IdEmpresa) return;

    let isActive = true;

    const fetchProveidorsProducte = async () => {
      try {
        const resp = await fetch(`${API_URL}/Proveidorsproducte.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idProductes: Number(selectedProduct),
            idEmpresa: empresaSeleccionada.IdEmpresa,
            nomesActius: 1
          })
        });
        const data = await resp.json();
        if (!isActive) return;

        const llista = data.success && Array.isArray(data.proveidors)
          ? [...data.proveidors].sort(
              (a, b) => preuEfectiuProveidor(a) - preuEfectiuProveidor(b)
            )
          : [];
        setProveidorsOptions(llista);

        if (!llista.length) {
          if (!producteProveidor) {
            alert(
              "⚠ No s'ha trobat cap preu per aquest producte.\n\nS'activarà el mode manual."
            );
            setPreuMaterialProveidor(0);
            setPreuMaterialManual(0);
            setPreuPrioritariMaterial("Manual");
            setNomProveidor("");
          }
          return;
        }

        const actual = producteProveidor
          ? llista.find(
              (p) => Number(p.idProductesProveidors) === Number(producteProveidor)
            )
          : null;
        const escollit = actual || llista[0];
        if (!actual) {
          setProducteProveidor(Number(escollit.idProductesProveidors));
          setPreuOriginalProducte(preuEfectiuProveidor(escollit));
          setQuantitatProducte(1);
          setNomProveidor(nomProveidorEntitat(escollit));
          if (preuPrioritariMaterial !== "Manual") {
            setPreuPrioritariMaterial("Proveidor");
          }
        }
      } catch (error) {
        if (isActive) {
          console.error("Error carregant proveïdors del producte:", error);
          if (!producteProveidor) {
            setPreuMaterialProveidor(0);
            setNomProveidor("");
            setProveidorsOptions([]);
          }
        }
      }
    };

    fetchProveidorsProducte();

    return () => {
      isActive = false;
    };
    // producteProveidor no va a deps: si ja n'hi ha (modificar / material mínim) es conserva;
    // si és null (canvi de producte) s'escull el més econòmic.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    eleccioProducte,
    selectedProduct,
    mode,
    empresaSeleccionada?.IdEmpresa,
    isReadOnly
  ]);

  // =======================================================================
  //  4c) Preus per Oferta
  // =======================================================================
  useEffect(() => {
    if (isReadOnly || mode === "esborrar") return;
    if (eleccioProducte !== "Oferta") return;

    if (
      selectedOferta &&
      selectedProducteOferta &&
      empresaSeleccionada?.IdEmpresa
    ) {
      const ofertaTrovada = ofertesList.find(
        (oferta) => oferta.idOfertes === selectedOferta
      );

      const producteOfertaTrovat = productesOfertaList.find(
        (producte) => producte.idProductesOfertes === selectedProducteOferta
      );

      if (ofertaTrovada && producteOfertaTrovat) {
        setPreuOriginalProducte(producteOfertaTrovat.PreuUnitari);
        setPreuMaterialProveidor(producteOfertaTrovat.PreuUnitari);
        setNomProveidor(ofertaTrovada.NomProveidor || "");
        setSelectedMaterial(producteOfertaTrovat.Materials_idMaterials);
        setSelectedProduct(producteOfertaTrovat.Productes_idProductes);
        setMarcaModel(
          `${producteOfertaTrovat.marca || ""} ${
            producteOfertaTrovat.Model || ""
          }`.trim()
        );

        setPreuPrioritariMaterial("Oferta");

        const producteProveidorLocal = productCatalog.find(
          (productecat) =>
            productecat.idProductes ===
            producteOfertaTrovat.Productes_idProductes
        );
        if (producteProveidorLocal) {
          setSelectedCategoria(producteProveidorLocal.Categoria);
        }
      } else {
        setPreuOriginalProducte(0);
        setPreuMaterialProveidor(0);
        setPreuMaterialManual(0);
        setPreuPrioritariMaterial("Oferta");
        setNomProveidor("");
        setMarcaModel("");
      }
    } else {
      // si som a Oferta però encara no hi ha selecció no cal tocar res més
    }
  }, [
    eleccioProducte,
    selectedOferta,
    selectedProducteOferta,
    mode,
    productCatalog,
    empresaSeleccionada?.IdEmpresa,
    isReadOnly,
    ofertesList,
    productesOfertaList
  ]);

  // =======================================================================
  //  5) Càlcul del Preu Unitari i Preu Total
  // =======================================================================
  const calcularPreu = useCallback(() => {
    const mat =
      preuPrioritariMaterial === "Manual"
        ? parseFloat(preuMaterialManual || 0)
        : parseFloat(preuMaterialProveidor || 0);

    const mao =
      preuPrioritariMaObra === "Manual"
        ? parseFloat(preuMaObraManual || 0)
        : parseFloat(preuMaObraAutomatic || 0);

    const ext = parseFloat(preuExtra || 0);

    const unitari = mat + mao + ext;
    const unitariRounded = Math.round(unitari * 100) / 100;

    const qtat =
      quantitat === "" || quantitat === null || quantitat === undefined
        ? 0
        : parseFloat(quantitat) || 0;
    const total = unitariRounded * qtat;
    const totalRounded = Math.round(total * 100) / 100;

    setPreuUnitari(unitariRounded);
    setPreuTotal(totalRounded);
  }, [
    preuPrioritariMaterial,
    preuMaterialManual,
    preuMaterialProveidor,
    preuPrioritariMaObra,
    preuMaObraManual,
    preuMaObraAutomatic,
    preuExtra,
    quantitat
  ]);

  useEffect(() => {
    if (!isReadOnly) {
      calcularPreu();
    }
  }, [calcularPreu, isReadOnly]);

  // Càlcul Mà d'Obra automàtica
  useEffect(() => {
    if (!selectedGrupMaObra) {
      setPreuMaObraAutomatic(0);
      return;
    }

    const grupSeleccionat = grupMaObraList.find(
      (g) => g.idGrupMaObra === Number(selectedGrupMaObra)
    );
    const preuHora = grupSeleccionat ? grupSeleccionat.PreuGrup : 0;

    const preuCalculat = calculMaObra(
      preuHora,
      unitatsTemps,
      parseFloat(duracioEstimada) || 0
    );
    setPreuMaObraAutomatic(preuCalculat);
  }, [selectedGrupMaObra, duracioEstimada, unitatsTemps, grupMaObraList]);

  // Quantitat producte -> preu material proveïdor
  useEffect(() => {
    const qtat = parseFloat(quantitatProducte || 1);
    const preuTotalProducte = qtat * preuOriginalProducte;
    const preuArrodonit = Math.round(preuTotalProducte * 100) / 100;
    setPreuMaterialProveidor(preuArrodonit);
  }, [quantitatProducte, preuOriginalProducte]);

  // =======================================================================
  //  6) Handlers relacionats amb preus i seleccions
  // =======================================================================
  const handleChangePreuManualMaterial = (valor) => {
    setPreuMaterialManual(valor);
    const num = parseFloat(valor);
    if (valor !== "" && !isNaN(num) && num !== 0) {
      setPreuPrioritariMaterial("Manual");
    } else if (preuPrioritariMaterial === "Manual") {
      setPreuPrioritariMaterial(
        eleccioProducte === "Oferta" ? "Oferta" : "Proveidor"
      );
    }
  };

  const handleResetPreuProveidorMaterial = () => {
    setPreuMaterialManual(0);
    setPreuPrioritariMaterial(
      eleccioProducte === "Oferta" ? "Oferta" : "Proveidor"
    );
  };

  const handleChangeProveidorMaterial = (idProductesProveidors) => {
    const idNum = idProductesProveidors ? Number(idProductesProveidors) : null;
    setProducteProveidor(idNum);

    const prov = (proveidorsOptions || []).find(
      (p) => Number(p.idProductesProveidors) === idNum
    );
    if (prov) {
      setPreuOriginalProducte(preuEfectiuProveidor(prov));
      setQuantitatProducte(1);
      setNomProveidor(nomProveidorEntitat(prov));
      if (preuPrioritariMaterial !== "Manual") {
        setPreuPrioritariMaterial(
          eleccioProducte === "Oferta" ? "Oferta" : "Proveidor"
        );
      }
    }
  };

  const handleChangePreuMaObraManual = (valor) => {
    setPreuMaObraManual(valor);
    const num = parseFloat(valor);
    if (valor !== "" && !isNaN(num) && num !== 0) {
      setPreuPrioritariMaObra("Manual");
    } else if (preuPrioritariMaObra === "Manual") {
      setPreuPrioritariMaObra("Automatic");
    }
  };

  const handleReciclarPreuMaObraAutomatic = () => {
    setPreuMaObraManual(0);
    setPreuPrioritariMaObra("Automatic");
  };

  const clearSeleccioMaterialProducte = () => {
    setSelectedCategoria("");
    setSelectedTipus("");
    setSelectedMaterial(null);
    setSelectedProduct(null);
    setSelectedOferta(null);
    setSelectedProducteOferta(null);

    setProducteProveidor(null);
    setProveidorsOptions([]);
    setNomProveidor("");
    setMarcaModel("");
    setQuantitatProducte(1);
    setPreuOriginalProducte(0);
    setPreuMaterialProveidor(0);
    setPreuMaterialManual(0);
    setPreuPrioritariMaterial(
      eleccioProducte === "Oferta" ? "Oferta" : "Proveidor"
    );
  };

  const onChangeProduct = (idProducteSeleccionat) => {
    setSelectedProduct(idProducteSeleccionat);
    setProducteProveidor(null);
    setProveidorsOptions([]);
    setNomProveidor("");
    setMarcaModel("");
    setPreuOriginalProducte(0);
    setPreuMaterialProveidor(0);

    if (idProducteSeleccionat) {
      const producteTrobat = productCatalog.find(
        (p) => p.idProductes === idProducteSeleccionat
      );
      if (producteTrobat) {
        setSelectedCategoria(producteTrobat.Categoria);
        setSelectedTipus(producteTrobat.Tipus);
        setSelectedMaterial(producteTrobat.idMaterials);
        setMarcaModel(
          `${producteTrobat.Marca || ""} ${producteTrobat.Model || ""}`.trim()
        );
      }
    }
  };

  const onChangeMaterial = (idMaterialSeleccionat) => {
    setSelectedMaterial(idMaterialSeleccionat);
    setProducteProveidor(null);
    setProveidorsOptions([]);
    setNomProveidor("");
    setMarcaModel("");
    setPreuOriginalProducte(0);
    setPreuMaterialProveidor(0);

    if (idMaterialSeleccionat) {
      const materialTrobat = productCatalog.find(
        (p) => p.idMaterials === idMaterialSeleccionat
      );
      if (materialTrobat) {
        setSelectedCategoria(materialTrobat.Categoria);
        setSelectedTipus(materialTrobat.Tipus);
      }
    }
  };

  const onChangeOferta = (idOfertaSeleccionada) => {
    setSelectedOferta(idOfertaSeleccionada);
    setSelectedProducteOferta(null);
    setProducteProveidor(null);
    setProveidorsOptions([]);
    setNomProveidor("");
    setMarcaModel("");
    setPreuOriginalProducte(0);
    setPreuMaterialProveidor(0);
  };

  const onChangeProducteOferta = (idProducteOfertaSeleccionat) => {
    setSelectedProducteOferta(idProducteOfertaSeleccionat);
    setProducteProveidor(null);

    const producteTrobat = productCatalog.find(
      (p) => p.idProductes === idProducteOfertaSeleccionat
    );
    if (producteTrobat) {
      setSelectedCategoria(producteTrobat.Categoria);
      setSelectedTipus(producteTrobat.Tipus);
      setSelectedMaterial(producteTrobat.idMaterials);
    }
  };

  // =======================================================================
  //  7) SUBMIT
  // =======================================================================
  const handleSubmit = async () => {
    if (!(codiPartida.trim() || codiPartidaDefault.trim())) {
      alert("El camp Partida (codi) és obligatori!");
      return;
    }
    const qtatValid =
      quantitat === "" || quantitat === null || quantitat === undefined
        ? 0
        : parseFloat(quantitat) || 0;
    if (qtatValid < 0) {
      alert("La quantitat ha de ser >= 0.");
      return;
    }

    const newData = {
      Partida: codiPartida || codiPartidaDefault,
      EsApartat: Number(esApartat),
      Quantitat: esApartat ? 1 : qtatValid,
      Unitats: unitatMesura,
      DescripcioPartida: descripcioPartida,

      EleccioProducte: eleccioProducte,
      PreuMaterialManual: parseFloat(preuMaterialManual || 0),
      PreuMaterialProducteProveidor: parseFloat(preuMaterialProveidor || 0),
      PreuPrioritariMaterial: preuPrioritariMaterial,
      Categoria: selectedCategoria,

      GrupMaObra_idGrupMaObra: selectedGrupMaObra,
      DuracioEstimadaUnitat: parseFloat(duracioEstimada || 0),
      UnitatsTemps: unitatsTemps,
      PreuMaObraManual: parseFloat(preuMaObraManual || 0),
      PreuMaObra: parseFloat(preuMaObraAutomatic || 0),
      PreuPrioritariMaObra: preuPrioritariMaObra,

      PreuExtra: parseFloat(preuExtra || 0),
      PreuUnitari: parseFloat(preuUnitari || 0),
      PreuTotal: parseFloat(preuTotal || 0),

      Materials_idMaterials: selectedMaterial,
      Productes_Proveidors_idProductesProveidors: producteProveidor,
      Productes_idProductes: selectedProduct,
      ProductesOfertes_idProductesOfertes: selectedProducteOferta,
      QuantitatProducte: parseFloat(quantitatProducte || 1),
      PreuOriginalProducte: parseFloat(preuOriginalProducte || 0),

      idPartidaPare: partidaPare?.idPartidesPressupost || null,
      Versions_idVersions: versio.idVersions,
      Versions_Pressupostos_idPressupostos: pressupost.idPressupostos
    };

    try {
      if (mode === "nou") {
        const resp = await fetch(`${API_URL}/Novapartidaversio.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(newData)
        });
        const data = await resp.json();
        if (data.success) {
          alert("Partida creada correctament!");
          onSave?.(data.partida || newData);
        } else {
          alert("Error en crear la partida: " + data.message);
        }
      } else if (mode === "modificar") {
        const resp = await fetch(`${API_URL}/Modificarpartidaversio.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...newData,
            idPartidesPressupost: partida?.idPartidesPressupost
          })
        });
        const data = await resp.json();
        if (data.success) {
          alert("Partida modificada correctament!");
          onSave?.(data.partida || { ...partida, ...newData });
        } else {
          alert("Error en modificar la partida: " + data.message);
        }
      } else if (mode === "esborrar") {
        const confirmar = window.confirm(
          "Segur que vols esborrar aquesta partida?"
        );
        if (!confirmar) return;
        const resp = await fetch(`${API_URL}/Esborrarpartidaversio.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idPartidesPressupost: partida?.idPartidesPressupost
          })
        });
        const data = await resp.json();
        if (data.success) {
          alert("Partida esborrada correctament!");
          onCancel?.();
        } else {
          alert("Error en esborrar la partida: " + data.message);
        }
      }
    } catch (error) {
      console.error("Error en handleSubmit partida versió:", error);
      alert(
        "S'ha produït un error en el procés de creació/modificació/esborrat."
      );
    }
  };

    // ---------------------------
  // 8) RENDERS DE BLOCS
  // ---------------------------

  const camp = (label, content, mida = "mig") => {
    const extra =
      mida === "ample"
        ? "dv2-field--ample"
        : mida === "llarg"
        ? "dv2-field--llarg"
        : mida === "curt"
        ? "dv2-field--curt"
        : "dv2-field--mig";
    return (
      <label className={`dv2-field ${extra}`}>
        <span className="dv2-label">{label}</span>
        {content}
      </label>
    );
  };

  const valor = (txt) => <span className="dv2-value">{txt || "—"}</span>;
  const brk = () => <div className="dv2-fields-break" />;

  const card = (num, title, children) => (
    <section className="dv2-card">
      <div className="dv2-card-head">
        <span className="dv2-card-num">{num}</span>
        <h2 className="dv2-card-title">{title}</h2>
      </div>
      <div className="dv2-fields dv2-fields--sizes">{children}</div>
    </section>
  );

  const renderBloc1 = () => {
    const labelCodi = esApartat ? "Codi apartat" : "Codi partida";
    const labelDesc = esApartat ? "Descripció apartat" : "Descripció partida";

    if (isReadOnly) {
      return card(1, "Identificació", (
        <>
          {camp(labelCodi, valor(codiPartida), "curt")}
          {!esApartat && camp("Quantitat", valor(quantitat), "curt")}
          {!esApartat && camp("Unitats", valor(unitatMesura), "curt")}
          {brk()}
          {camp(labelDesc, valor(descripcioPartida), "ample")}
        </>
      ));
    }

    return card(1, "Identificació", (
      <>
        {camp(
          labelCodi,
          <input
            type="text"
            className={`dv2-input${codiPartida === "" ? " is-placeholder" : ""}`}
            value={codiPartida || codiPartidaDefault}
            onChange={(e) => {
              const v = e.target.value;
              if (codiPartida === "" && codiPartidaDefault !== "") {
                setCodiPartida(v.replace(codiPartidaDefault, ""));
                setCodiPartidaDefault("");
              } else {
                setCodiPartida(v);
              }
            }}
            onClick={(e) => {
              if (codiPartida === "" && codiPartidaDefault !== "") {
                e.target.setSelectionRange(0, 0);
              }
            }}
          />,
          "curt"
        )}
        {!esApartat &&
          camp(
            "Quantitat",
            <input
              type="number"
              className="dv2-input"
              step="1"
              min="0"
              value={quantitat}
              autoFocus={!isApartat}
              onFocus={(e) => e.target.select()}
              onChange={(e) => setQuantitat(e.target.value)}
            />,
            "curt"
          )}
        {!esApartat &&
          camp(
            "Unitats",
            <select
              className="dv2-select"
              value={unitatMesura}
              onChange={(e) => setUnitatMesura(e.target.value)}
            >
              {unitatOptions.map((um) => (
                <option key={um} value={um}>
                  {um}
                </option>
              ))}
            </select>,
            "curt"
          )}
        {brk()}
        {camp(
          labelDesc,
          <textarea
            className="dv2-textarea"
            rows={3}
            value={descripcioPartida}
            autoFocus={!!isApartat}
            onChange={(e) => setDescripcioPartida(e.target.value)}
          />,
          "ample"
        )}
      </>
    ));
  };

  const renderBloc2 = () => {
    const preuMaterial =
      preuPrioritariMaterial === "Manual"
        ? preuMaterialManual
        : preuMaterialProveidor;

    if (isReadOnly) {
      let infoEleccio = "Material";
      if (eleccioProducte === "Producte") infoEleccio = "Producte";
      if (eleccioProducte === "Oferta") infoEleccio = "Oferta";
      return card(2, "Producte", (
        <>
          {camp("Elecció", valor(infoEleccio), "curt")}
          {camp("Marca / Model", valor(marcaModel), "llarg")}
          {brk()}
          {camp("Proveïdor", valor(nomProveidor ? `${nomProveidor} — ${formatNumero(preuOriginalProducte)} €` : ""), "llarg")}
          {camp("Quantitat producte", valor(formatNumero(quantitatProducte)), "curt")}
          {camp("Preu total material", valor(`${formatNumero(preuMaterial)} €`), "curt")}
          {preuPrioritariMaterial === "Manual"
            ? camp("Preu manual", valor(`${formatNumero(preuMaterialManual)} €`), "curt")
            : null}
        </>
      ));
    }

    const proveidorsSelect = [...(proveidorsOptions || [])];
    if (
      producteProveidor &&
      !proveidorsSelect.some(
        (p) => Number(p.idProductesProveidors) === Number(producteProveidor)
      )
    ) {
      proveidorsSelect.unshift({
        idProductesProveidors: producteProveidor,
        NomComercial: nomProveidor || `Proveïdor ${producteProveidor}`,
        PreuNet: preuOriginalProducte,
        RefProdProv: ""
      });
    }

    const handleChangeEleccio = (val) => {
      const prev = eleccioProducte;
      setEleccioProducte(val);

      if (val === "Oferta" || prev === "Oferta") {
        setSelectedCategoria("");
        setSelectedTipus("");
        setSelectedMaterial(null);
        setSelectedProduct(null);
        setSelectedOferta(null);
        setSelectedProducteOferta(null);
        setProducteProveidor(null);
        setProveidorsOptions([]);
        setNomProveidor("");
        setMarcaModel("");
        setPreuOriginalProducte(0);
        setPreuMaterialProveidor(0);
        setPreuMaterialManual(0);
        setPreuPrioritariMaterial(val === "Oferta" ? "Oferta" : "Proveidor");
      } else {
        setSelectedProduct(null);
        setProducteProveidor(null);
        setProveidorsOptions([]);
        setNomProveidor("");
        setMarcaModel("");
        setPreuOriginalProducte(0);
        setPreuMaterialProveidor(0);
        setPreuPrioritariMaterial("Proveidor");
      }
    };

    const selectCategoria = (extraReset) => (
      <select
        className="dv2-select"
        value={selectedCategoria}
        onChange={(e) => {
          setSelectedCategoria(e.target.value);
          setSelectedTipus("");
          setSelectedMaterial(null);
          extraReset?.();
          setProducteProveidor(null);
          setProveidorsOptions([]);
          setNomProveidor("");
          setMarcaModel("");
          setPreuOriginalProducte(0);
          setPreuMaterialProveidor(0);
        }}
      >
        <option value="">— Totes —</option>
        {categoriesList.map((cat) => (
          <option key={cat} value={cat}>
            {cat}
          </option>
        ))}
      </select>
    );

    const selectTipus = (extraReset) => (
      <select
        className="dv2-select"
        value={selectedTipus}
        onChange={(e) => {
          setSelectedTipus(e.target.value);
          setSelectedMaterial(null);
          extraReset?.();
          setProducteProveidor(null);
          setProveidorsOptions([]);
          setNomProveidor("");
          setMarcaModel("");
          setPreuOriginalProducte(0);
          setPreuMaterialProveidor(0);
        }}
      >
        <option value="">— Tots —</option>
        {tipusList.map((tip) => (
          <option key={tip} value={tip}>
            {tip}
          </option>
        ))}
      </select>
    );

    return card(2, "Producte", (
      <>
        {camp(
          "Elecció producte",
          <select
            className="dv2-select"
            value={eleccioProducte}
            onChange={(e) => handleChangeEleccio(e.target.value)}
          >
            <option value="Material">Material</option>
            <option value="Producte">Producte</option>
            <option value="Oferta">Oferta</option>
          </select>,
          "mig"
        )}
        {brk()}

        {eleccioProducte === "Material" && (
          <>
            {camp("Categoria", selectCategoria(), "llarg")}
            {camp("Tipus", selectTipus(), "mig")}
            {camp(
              "Material",
              <select
                className="dv2-select"
                value={selectedMaterial || ""}
                onChange={(e) => onChangeMaterial(Number(e.target.value) || null)}
              >
                <option value="">— Sense selecció —</option>
                {filteredMaterials.map((mat) => (
                  <option key={mat.idMaterials} value={mat.idMaterials}>
                    {mat.NomGeneric || mat.DescripcioMaterial}
                  </option>
                ))}
              </select>,
              "llarg"
            )}
          </>
        )}

        {eleccioProducte === "Producte" && (
          <>
            {camp(
              "Categoria",
              selectCategoria(() => setSelectedProduct(null)),
              "llarg"
            )}
            {camp(
              "Tipus",
              selectTipus(() => setSelectedProduct(null)),
              "mig"
            )}
            {camp(
              "Material",
              <select
                className="dv2-select"
                value={selectedMaterial || ""}
                onChange={(e) => {
                  setSelectedMaterial(e.target.value || null);
                  setSelectedProduct(null);
                  setProducteProveidor(null);
                  setProveidorsOptions([]);
                  setNomProveidor("");
                  setMarcaModel("");
                  setPreuOriginalProducte(0);
                  setPreuMaterialProveidor(0);
                }}
              >
                <option value="">— Tots —</option>
                {filteredMaterials.map((mat) => (
                  <option key={mat.idMaterials} value={mat.idMaterials}>
                    {mat.NomGeneric}
                  </option>
                ))}
              </select>,
              "llarg"
            )}
            {camp(
              "Producte",
              <select
                className="dv2-select"
                value={selectedProduct || ""}
                onChange={(e) => onChangeProduct(Number(e.target.value) || null)}
              >
                <option value="">— Sense selecció —</option>
                {filteredProducts.map((p) => (
                  <option key={p.idProductes} value={p.idProductes}>
                    {p.Marca} {p.Model}
                  </option>
                ))}
              </select>,
              "llarg"
            )}
          </>
        )}

        {eleccioProducte === "Oferta" && (
          <>
            {camp(
              "Oferta",
              <select
                className="dv2-select"
                value={selectedOferta || ""}
                onChange={(e) => onChangeOferta(Number(e.target.value) || null)}
              >
                <option value="">— Sense selecció —</option>
                {ofertesList.map((ofe) => (
                  <option key={ofe.idOfertes} value={ofe.idOfertes}>
                    {ofe.NomProveidor} - {ofe.ReferenciaProv} - ({formatDate(ofe.DataOferta)})
                  </option>
                ))}
              </select>,
              "llarg"
            )}
            {camp(
              "Producte de l'oferta",
              <select
                className="dv2-select"
                value={selectedProducteOferta || ""}
                onChange={(e) =>
                  onChangeProducteOferta(Number(e.target.value) || null)
                }
              >
                <option value="">— Sense selecció —</option>
                {filteredProductesOferta.map((pof) => (
                  <option key={pof.idProductesOfertes} value={pof.idProductesOfertes}>
                    {pof.Marca} {pof.Model} ({pof.PreuUnitari} €)
                  </option>
                ))}
              </select>,
              "llarg"
            )}
          </>
        )}

        {brk()}
        {eleccioProducte !== "Oferta" && (selectedProduct || selectedMaterial) && (
          <>
            {camp(
              "Proveïdor",
              proveidorsSelect.length ? (
                <select
                  className="dv2-select"
                  value={producteProveidor || ""}
                  onChange={(e) => handleChangeProveidorMaterial(e.target.value || null)}
                >
                  {proveidorsSelect.map((p) => (
                    <option key={p.idProductesProveidors} value={p.idProductesProveidors}>
                      {etiquetaProveidorOpcio(p)}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="dv2-value" style={{ color: "#b00020", fontWeight: 600 }}>
                  Sense preu assignat
                </span>
              ),
              "llarg"
            )}
            {camp(
              "Quantitat producte",
              <input
                type="number"
                className="dv2-input"
                step="0.5"
                min="0"
                value={quantitatProducte}
                onChange={(e) => setQuantitatProducte(e.target.value)}
              />,
              "curt"
            )}
            {camp(
              "Preu total material",
              <span className="dv2-value" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                {formatNumero(preuMaterialProveidor)} €
                <button
                  type="button"
                  className="dv2-btn dv2-btn--icon dv2-btn--ghost"
                  onClick={handleResetPreuProveidorMaterial}
                  title="Tornar a preu de proveïdor (neteja manual)"
                >
                  ♻
                </button>
              </span>,
              "curt"
            )}
            {camp(
              "Preu manual",
              <input
                type="number"
                className="dv2-input"
                step="0.01"
                value={preuMaterialManual}
                onChange={(e) => handleChangePreuManualMaterial(e.target.value)}
              />,
              "curt"
            )}
          </>
        )}

        {eleccioProducte === "Oferta" && (
          <>
            {camp(
              "Quantitat producte",
              <input
                type="number"
                className="dv2-input"
                step="0.5"
                min="0"
                value={quantitatProducte}
                onChange={(e) => setQuantitatProducte(e.target.value)}
              />,
              "curt"
            )}
            {camp(
              "Preu total material",
              valor(`${formatNumero(preuMaterialProveidor)} €`),
              "curt"
            )}
            {camp(
              "Preu manual",
              <input
                type="number"
                className="dv2-input"
                step="0.01"
                value={preuMaterialManual}
                onChange={(e) => handleChangePreuManualMaterial(e.target.value)}
              />,
              "curt"
            )}
          </>
        )}

        {eleccioProducte !== "Oferta" && !selectedProduct && !selectedMaterial &&
          camp(
            "Preu manual",
            <input
              type="number"
              className="dv2-input"
              step="0.01"
              value={preuMaterialManual}
              onChange={(e) => handleChangePreuManualMaterial(e.target.value)}
            />,
            "curt"
          )}

        {((eleccioProducte === "Material" && selectedMaterial) ||
          (eleccioProducte === "Producte" && selectedProduct) ||
          (eleccioProducte === "Oferta" && selectedProducteOferta)) && (
          <>
            {brk()}
            <div className="dv2-field">
              <button
                type="button"
                className="dv2-btn"
                onClick={clearSeleccioMaterialProducte}
                title="Buidar selecció de material/producte i deixar preu manual"
              >
                Buidar
              </button>
            </div>
          </>
        )}
      </>
    ));
  };

  const renderBloc3 = () => {
    const preuMaObra =
      preuPrioritariMaObra === "Manual" ? preuMaObraManual : preuMaObraAutomatic;
    const grup = grupMaObraList.find((g) => g.idGrupMaObra === selectedGrupMaObra);

    if (isReadOnly) {
      return card(3, "Mà d'obra", (
        selectedGrupMaObra ? (
          <>
            {camp("Grup", valor(grup?.Descripcio || grup?.Nom || "Sense grup"), "llarg")}
            {camp("Duració estimada", valor(`${duracioEstimada} ${unitatsTemps}`), "mig")}
            {camp("Preu mà d'obra", valor(`${formatNumero(preuMaObra)} €`), "curt")}
          </>
        ) : (
          camp("Grup", valor("Sense grup de mà d'obra"), "ample")
        )
      ));
    }

    return card(3, "Mà d'obra", (
      <>
        {camp(
          "Grup de mà d'obra",
          <select
            className="dv2-select"
            value={selectedGrupMaObra || ""}
            onChange={(e) => {
              const val = e.target.value || null;
              setSelectedGrupMaObra(val);
            }}
          >
            <option value="">— Sense selecció —</option>
            {grupMaObraList.map((g) => (
              <option key={g.idGrupMaObra} value={g.idGrupMaObra}>
                {g.Nom} ({g.PreuGrup} €/h)
              </option>
            ))}
          </select>,
          "llarg"
        )}
        {selectedGrupMaObra && (
          <>
            {camp(
              "Duració estimada",
              <input
                type="number"
                className="dv2-input"
                step="0.01"
                value={duracioEstimada}
                onChange={(e) => setDuracioEstimada(e.target.value)}
              />,
              "curt"
            )}
            {camp(
              "Unitats de temps",
              <select
                className="dv2-select"
                value={unitatsTemps}
                onChange={(e) => setUnitatsTemps(e.target.value)}
              >
                <option value="Minuts">Minuts</option>
                <option value="Hores">Hores</option>
                <option value="Dies">Dies</option>
                <option value="Setmanes">Setmanes</option>
                <option value="Mesos">Mesos</option>
              </select>,
              "curt"
            )}
            {camp(
              "Preu automàtic",
              <span className="dv2-value" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                {formatNumero(preuMaObraAutomatic)} €
                <button
                  type="button"
                  className="dv2-btn dv2-btn--icon dv2-btn--ghost"
                  onClick={handleReciclarPreuMaObraAutomatic}
                  title="Tornar a automàtic"
                >
                  ♻
                </button>
              </span>,
              "curt"
            )}
          </>
        )}
        {brk()}
        {camp(
          "Preu manual mà d'obra",
          <input
            type="number"
            className="dv2-input"
            step="0.01"
            value={preuMaObraManual}
            onChange={(e) => handleChangePreuMaObraManual(e.target.value)}
          />,
          "curt"
        )}
      </>
    ));
  };

  const renderBloc4 = () => {
    if (isReadOnly) {
      return card(4, "Extra", (
        camp("Preu extra", valor(`${formatNumero(preuExtra)} €`), "curt")
      ));
    }
    return card(4, "Extra", (
      camp(
        "Preu extra",
        <input
          type="number"
          className="dv2-input"
          step="0.01"
          value={preuExtra}
          onChange={(e) => setPreuExtra(e.target.value)}
        />,
        "curt"
      )
    ));
  };

  const renderBloc5 = () => {
    const mat =
      preuPrioritariMaterial === "Manual"
        ? parseFloat(preuMaterialManual || 0)
        : parseFloat(preuMaterialProveidor || 0);
    const mao =
      preuPrioritariMaObra === "Manual"
        ? parseFloat(preuMaObraManual || 0)
        : parseFloat(preuMaObraAutomatic || 0);
    const ext = parseFloat(preuExtra || 0);

    return card(5, "Totals", (
      <>
        {camp(
          "Preu unitari",
          valor(
            `${formatNumero(preuUnitari)} €  (Prod: ${formatNumero(mat)} € + Mà d'obra: ${formatNumero(mao)} € + Extra: ${formatNumero(ext)} €)`
          ),
          "ample"
        )}
        {brk()}
        {camp(
          "Preu total",
          valor(
            `${formatNumero(preuTotal)} €  (${quantitat} × ${formatNumero(preuUnitari)} €)`
          ),
          "ample"
        )}
      </>
    ));
  };

  const potNavegar = mode !== "nou" && onNavigate && recorregut.length > 1 && currentIndex >= 0;
  const esEdicio = mode === "nou" || mode === "modificar" || mode === "esborrar";

  return (
    <>
      {mode === "esborrar" && (
        <div className="dv2-banner dv2-banner--danger">
          {esApartat
            ? "S'esborrarà aquest apartat. Si té fills, també s'esborraran."
            : "S'esborrarà aquesta partida. Si té fills, també s'esborraran."}
        </div>
      )}
      <div className="dv2-entity-body">
        {renderBloc1()}
        {!esApartat && renderBloc2()}
        {!esApartat && renderBloc3()}
        {!esApartat && renderBloc4()}
        {!esApartat && renderBloc5()}
      </div>
      {(potNavegar || esEdicio) && (
        <div className="dv2-entity-dock">
          {potNavegar && (
            <div className="dv2-recordNav">
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={anteriorPartida} title="Anterior">
                ‹
              </button>
              <span className="dv2-recordNav-info">
                {currentIndex + 1} / {recorregut.length}
              </span>
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={seguentPartida} title="Següent">
                ›
              </button>
            </div>
          )}
          {esEdicio && (
            <div className="dv2-footerActions">
              <button type="button" className="dv2-btn" onClick={onCancel}>
                Cancel·lar
              </button>
              {mode === "esborrar" ? (
                <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleSubmit}>
                  {esApartat ? "Esborrar apartat" : "Esborrar partida"}
                </button>
              ) : (
                <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
                  {mode === "nou" ? (esApartat ? "Desar apartat" : "Desar partida") : "Desar"}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default DetallsPartidaVersio;
